/**
 * TaskDetail.tsx — karta zadania (faza 30, ADR-0123): podgląd, tworzenie, edycja.
 *
 * Wzorzec: `EmployeeDetail.tsx` — formularz składa silnik formatek (`taskEngineConfig`,
 * układ „Default" z seeda), a zapis jest ATOMOWY: nagłówek + pola dodatkowe + delty tabel
 * dodatkowych jadą w JEDNYM POST / PATCH (zasada zapisu kartoteki).
 *
 * Tworzenie: „Dodaj" na liście otwiera okno wyboru typu, potem `/tasks/new?taskTypeId=…`.
 * Wejście na `/tasks/new` bez (poprawnego) typu otwiera to samo okno. Firma zadania wynika z typu —
 * formularz nie ma pola Firma, żądanie nie niesie `companyId`. Formularz nowego zadania startuje
 * z wartości domyślnych pól (Ustawienia kartoteki → Pola), wczytanych po wyborze typu.
 * „Zmień typ" (przycisk przy polu Typ) — to samo okno, PATCH z nowym typem; firma zostaje, numer
 * nadaje serwer według numeracji nowego typu.
 *
 * Uprawnienia to podpowiedź (rozstrzyga serwer): przyciski Edytuj / Usuń zależą od
 * `permissions.canEdit` / `canDelete` z odpowiedzi API — bez prawa są nieaktywne z wyjaśnieniem.
 * Bez archiwum — tylko „Usuń" (miękkie). Usunięte zadanie (otwierane linkiem, z prawem „widok
 * usuniętych") jest tylko do odczytu; „Przywróć" (jak w karcie pisma) widać przy `canDelete`.
 *
 * Procedury kartoteki tenanta (jak karta kontrahenta): zapis, „Zmień typ" i usunięcie obsługują
 * ostrzeżenie procedury kontroli (`useControlWarning` — okno „Zapisz / Usuń mimo to" ponawia żądanie
 * z akceptacją) i błąd kontroli (komunikat). Klocek „Przycisk SQL" (`sql_button`) zleca zadanie w tle
 * (`useRecordProcedureRuns`): w edycji po zapisie, w podglądzie od razu, na nowym zadaniu — po
 * utworzeniu, przed przejściem pod adres zadania. Na czas zadania w tle pasek blokady, „Edytuj",
 * „Usuń" i „Przywróć" nieaktywne; odmowa serwera `RECORD_LOCKED_BY_RUN` — czytelny komunikat.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TASK_CORE_DATE_FIELDS } from '@saas/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, RotateCcw, Save, Trash2, X as XIcon } from 'lucide-react';

import { AdditionalTableTab } from '@/components/additional/AdditionalTableTab';
import { AdditionalViewTab } from '@/components/additional/AdditionalViewTab';
import { Alert } from '@/components/Alert';
import { AttachmentsSection } from '@/components/attachments/AttachmentsSection';
import { buildDocumentPreviewContextRenderer } from '@/components/attachments/DocumentPreviewContextRenderer';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  type TopUpContext,
  topUpDocumentLayout,
} from '@/components/documents/engine/topUpDocumentLayout';
import {
  type ContextRenderers,
  RecursiveFormRenderer,
} from '@/components/form/RecursiveFormRenderer';
import { SaveFirstNotice } from '@/components/form/SaveFirstNotice';
import { IconButton } from '@/components/IconButton';
import {
  buildCoreFieldRecordValues,
  collectEngineFieldErrors,
} from '@/components/kartoteki/engine/engine-config';
import { useKartotekaRenderField } from '@/components/kartoteki/engine/useKartotekaRenderField';
import { PermissionButton } from '@/components/permissions/PermissionGate';
import { ProcessApprovalBanner } from '@/components/processes/ProcessApprovalBanner';
import { ProcessAutostartIndicator } from '@/components/processes/ProcessAutostartIndicator';
import { buildProcessLayoutRenderers } from '@/components/processes/processLayoutRenderers';
import { NewRecordDefaultsStatus } from '@/components/record-defaults/NewRecordDefaultsStatus';
import {
  parseRecordLinksElementConfig,
  RecordLinksSection,
} from '@/components/record-links/RecordLinksSection';
import { RecordRunLockBanner } from '@/components/record-procedures/RecordRunLockBanner';
import {
  collectSqlButtonLabels,
  parseSqlButtonSettings,
} from '@/components/record-procedures/sql-button-settings';
import { SqlButton } from '@/components/record-procedures/SqlButton';
import { Spinner } from '@/components/Spinner';
import { SystemModuleAuditTab } from '@/components/system-modules/SystemModuleAuditTab';
import {
  buildTaskCreateBody,
  buildTaskPatchBody,
  emptyTaskForm,
  invalidTaskDateFields,
  rebaseTaskForm,
  type TaskFormData,
  taskFormFromDetail,
} from '@/components/tasks/task-form';
import { taskEngineConfig, type TaskExtra } from '@/components/tasks/taskEngineConfig';
import { TaskTypePickerModal } from '@/components/tasks/TaskTypePickerModal';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { invalidateRecordInstances } from '@/hooks/useActiveInstanceForRecord';
import { useAdditionalTableDeltas } from '@/hooks/useAdditionalTableDeltas';
import { useControlWarning } from '@/hooks/useControlWarning';
import { useFieldErrors } from '@/hooks/useFieldErrors';
import { applyCoreDefaults, useNewRecordDefaults } from '@/hooks/useNewRecordDefaults';
import { extValuesDiffer, useRecordExtValues } from '@/hooks/useRecordExtValues';
import { useRecordProcedureRuns } from '@/hooks/useRecordProcedureRuns';
import { useRecordProcessAutostart } from '@/hooks/useRecordProcessAutostart';
import { useSaveBeforeDecision } from '@/hooks/useSaveBeforeDecision';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { buildAdditionalViewRecordValues } from '@/lib/additional-views/record-values';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';
import { useSystemModuleFields } from '@/lib/ext-list-columns';
import { type ProcessWarning, processWarningMessage } from '@/lib/processes/approvals-api';
import { TASKS_MODULE_CODE } from '@/lib/processes/module-routes';
import {
  parseTaskApiError,
  TASK_DETAIL_KEY,
  type TaskDetailDto,
  type TaskDetailResponse,
  taskErrorMessage,
  TASKS_QUERY_ROOT,
  useTaskDetail,
  useTaskTypes,
} from '@/lib/tasks/task-api';
import { taskSaveErrorMessage } from '@/lib/tasks/task-save-errors';
import { useTaskNumberingFieldLabel } from '@/lib/tasks/useTaskNumberingBaseFieldOptions';
import { useMyModules } from '@/lib/use-my-modules';
import { buildFieldIssueMessages, resolveFieldLabel } from '@/lib/validation-toast';

import { PRIORITY_TONE, priorityLabel, STATUS_TONE, statusLabel } from './task-labels';

const MODULE_SLUG = 'tasks';

/** Pole formularza, pod którym pokazujemy błąd o danym kodzie serwera. */
const FIELD_BY_ERROR_CODE: Readonly<Record<string, keyof TaskFormData>> = {
  OWNER_NOT_IN_COMPANY: 'ownerUserId',
  CONTRACTOR_NOT_IN_COMPANY: 'contractorId',
  CATEGORY_INVALID: 'categoryId',
};

/** Kod błędu przywracania zadania → komunikat właściwy dla przywracania (reszta: wspólne teksty). */
const RESTORE_ERROR_KEYS: Readonly<Record<string, string>> = {
  TASK_TYPE_DELETED: 'tenant.tasks.detail.toast.restoreTypeDeleted',
  TASK_TYPE_INACTIVE: 'tenant.tasks.detail.toast.restoreTypeInactive',
  TASK_NUMBER_TAKEN: 'tenant.tasks.detail.toast.restoreNumberTaken',
};

/** Zapis / usunięcie — ponowienie po ostrzeżeniu procedury kontroli niesie `warningsAccepted`. */
interface SaveVariables {
  warningsAccepted?: boolean;
}

/** „Zmień typ" — nowy typ (+ akceptacja ostrzeżenia procedury kontroli przy ponowieniu). */
interface ChangeTypeVariables {
  taskTypeId: string;
  warningsAccepted?: boolean;
}

/** Slug pola daty numeracji z błędu serwera → klucz błędu pola (rdzenne: klucz formularza; dodatkowe: slug). */
function numberingBaseFormKey(slug: string): string {
  return TASK_CORE_DATE_FIELDS.find((f) => f.slug === slug)?.payloadKey ?? slug;
}

export function TaskDetail() {
  const { id } = useParams<{ id?: string }>();
  return <TaskDetailPage key={id ?? 'new'} taskId={id ?? null} />;
}

function TaskDetailPage({ taskId }: { taskId: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const withMenuItem = useWithMenuItem();
  const { me } = useTenantAuth();
  const fe = useFieldErrors();
  const [searchParams, setSearchParams] = useSearchParams();

  const isCreate = taskId === null;

  // Baner akceptacji na karcie — tylko gdy tenant ma aktywny moduł Procesy (jak karta kontrahenta).
  const { data: myModules } = useMyModules();
  const processesActive = myModules?.subscriptions.some((s) => s.code === 'processes') ?? false;
  // Wskaźnik „proces się uruchamia…" po create z automatyzacją start_approval (uzbrajany hintem
  // `processAutoStarting` z odpowiedzi POST /api/tasks, przekazanym w stanie nawigacji).
  const autostart = useRecordProcessAutostart(TASKS_MODULE_CODE, taskId ?? '');

  const [tab, setTab] = useState('data');
  const [editing, setEditing] = useState(isCreate);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [changeTypeOpen, setChangeTypeOpen] = useState(false);
  // Typ wybrany w „Zmień typ", dopóki zapis trwa — karta już pokazuje układ docelowego typu.
  const [pendingTypeId, setPendingTypeId] = useState<string | null>(null);
  // Trwa odświeżanie stanu serwera po błędzie zapisu — „Zapisz" czeka, żeby nie poszedł zapis
  // ze starą bazą zmian.
  const [isRebasing, setIsRebasing] = useState(false);

  // ── Dane: typy (tworzenie) i rekord (podgląd / edycja) ─────────────────────
  const typesQ = useTaskTypes({ activeOnly: true });
  const typeIdParam = searchParams.get('taskTypeId');
  const createType = useMemo(
    () => (isCreate ? (typesQ.data?.find((x) => x.id === typeIdParam) ?? null) : null),
    [isCreate, typesQ.data, typeIdParam],
  );
  const detailQ = useTaskDetail(taskId);
  const task: TaskDetailDto | undefined = detailQ.data?.task;
  const canEditRecord = detailQ.data?.permissions.canEdit ?? false;
  const canDeleteRecord = detailQ.data?.permissions.canDelete ?? false;
  const isDeleted = task !== undefined && task.gcrecord !== null;

  // Układ karty wskazuje TYP zadania: przy tworzeniu wybrany typ, w „Zmień typ" typ docelowy,
  // inaczej typ zapisanego zadania (jak karta dokumentu — typ w parametrze `typeId`).
  const layoutTypeId = isCreate
    ? (createType?.id ?? null)
    : (pendingTypeId ?? task?.taskTypeId ?? null);
  const { layoutConfig, myRoleId, isSuperUser } = useSystemModuleActiveLayout(MODULE_SLUG, {
    typeId: layoutTypeId,
  });

  // Przycisk SQL (klocek `sql_button`): zadania wykonywane w tle na tym zadaniu. Stan (aktywne zadanie
  // + ostatnie per procedura) odpytywany co ~2 s tylko przy aktywnym zadaniu; po jego końcu komunikat w
  // rogu i odświeżenie rekordu (zapytanie mogło go zmienić). Przy tworzeniu brak rekordu. Napisy
  // przycisków z układu służą komunikatom i paskowi blokady (zadanie zna tylko nazwę procedury).
  const sqlButtonLabels = useMemo(() => collectSqlButtonLabels(layoutConfig), [layoutConfig]);
  const runs = useRecordProcedureRuns('task', taskId, {
    labels: sqlButtonLabels,
    onFinished: () => {
      if (taskId !== null) {
        void queryClient.invalidateQueries({ queryKey: TASK_DETAIL_KEY(taskId) });
      }
      void queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_ROOT, 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      void queryClient.invalidateQueries({
        queryKey: ['system-module-audit', MODULE_SLUG, taskId],
      });
    },
  });
  // Przycisk kliknięty na NOWYM zadaniu: najpierw utworzenie (zwykły zapis), potem zadanie z id świeżo
  // utworzonego zadania, dopiero potem przejście pod jego adres (patrz `saveMutation.onSuccess`). `settle`
  // kończy oczekiwanie przycisku (kręciołek), także przy porażce zapisu.
  const pendingCreateRunRef = useRef<{ procedureId: string; settle: () => void } | null>(null);

  const currentUserName = me?.user.displayName ?? me?.user.email ?? null;
  const [form, setForm] = useState<TaskFormData>(() => emptyTaskForm('', currentUserName));
  // Stan zhydratowany z serwera — baza porównania dla PATCH (tylko zmienione pola).
  const [baseForm, setBaseForm] = useState<TaskFormData>(form);
  const hydratedRecordIdRef = useRef<string | null>(null);
  // Typ, dla którego formularz nowego zadania jest już zainicjowany (stan, nie ref — karta czeka
  // z polami, aż inicjalizacja nastąpi, żeby nie mignął formularz bez wartości domyślnych).
  const [initializedTypeId, setInitializedTypeId] = useState<string | null>(null);

  // Wartości domyślne pól (Ustawienia kartoteki → Pola) — żądane dopiero po wyborze typu.
  const recordDefaults = useNewRecordDefaults(MODULE_SLUG, isCreate && createType !== null);

  // Nowe zadanie: formularz po wyborze typu (raz na typ), startujący z wartości domyślnych pól
  // (nadpisują wartości startowe z kodu); typ i autor z kontekstu nakładamy PO nich.
  useEffect(() => {
    if (!isCreate) return;
    if (createType === null) {
      // Typ zniknął (adres bez typu, typ nieaktywny) — karta wraca do wyboru typu, a wartości
      // domyślne wczytają się od nowa; ponowny wybór tego samego typu buduje formularz od nowa.
      setInitializedTypeId(null);
      return;
    }
    if (recordDefaults.status !== 'ready') return;
    if (initializedTypeId === createType.id) return;
    const next: TaskFormData = {
      ...applyCoreDefaults(emptyTaskForm(createType.id, currentUserName), recordDefaults.core),
      taskTypeId: createType.id,
      createdByName: currentUserName,
    };
    setForm(next);
    setBaseForm(next);
    setInitializedTypeId(createType.id);
  }, [
    isCreate,
    createType,
    currentUserName,
    recordDefaults.status,
    recordDefaults.core,
    initializedTypeId,
  ]);

  // Zapisane zadanie: hydratacja z serwera — nie nadpisuje trwającej edycji tego samego rekordu.
  useEffect(() => {
    if (isCreate || task === undefined) return;
    if (editing && hydratedRecordIdRef.current === task.id) return;
    hydratedRecordIdRef.current = task.id;
    const next = taskFormFromDetail(task);
    setForm(next);
    setBaseForm(next);
    // `editing` celowo poza zależnościami: hydratacja reaguje na dane z serwera, nie na przełączenie trybu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, isCreate]);

  // ── Pola dodatkowe i tabele dodatkowe (bufor delt, jeden zapis) ────────────
  const { extValues, handleExtChange, snapshotEdits, markSaved, discardEdits } = useRecordExtValues(
    {
      recordId: isCreate ? undefined : (taskId ?? undefined),
      // Przy tworzeniu „wartością serwera” są wartości domyślne pól dodatkowych.
      serverExtValues: isCreate ? recordDefaults.ext : task?.extValues,
      serverVersion: detailQ.dataUpdatedAt,
    },
  );
  const {
    handleAdditionalDeltasChange,
    getBufferedValue,
    flattenAdditionalDeltas,
    buildAdditionalParentValues,
    resetAdditionalDeltas,
    getAddRowDraft,
    handleAddRowDraftChange,
    resetAddRowDrafts,
  } = useAdditionalTableDeltas();

  // ── Silnik formatek ────────────────────────────────────────────────────────
  const companyId = isCreate ? (createType?.companyId ?? null) : (task?.companyId ?? null);
  const taskTypeLabel = isCreate
    ? createType
      ? `${createType.name} (${createType.code})`
      : ''
    : task
      ? `${task.taskTypeName} (${task.taskTypeCode})`
      : '';
  // Rekord zablokowany na czas zadania przycisku SQL: aktywne zadanie albo właśnie wysyłane zlecenie —
  // „Edytuj”, „Usuń”, „Przywróć” i „Zmień typ” nieaktywne (serwer odrzuca zapis, zmianę typu, usunięcie
  // i przywrócenie tak czy inaczej).
  const recordLocked = runs.active !== null || runs.starting;
  const canChangeType = !isCreate && !isDeleted && canEditRecord && !recordLocked;

  const taskExtra: TaskExtra = {
    companyId,
    taskTypeLabel,
    onChangeType: canChangeType ? () => setChangeTypeOpen(true) : null,
  };
  const { renderField, fields: catalogFields } = useKartotekaRenderField<TaskFormData, TaskExtra>(
    taskEngineConfig,
    {
      form,
      onChange: setForm,
      extValues,
      onExtChange: handleExtChange,
      editable: editing || isCreate,
      isCreate,
      companyId,
      hasError: (key) => !!fe.get(String(key)),
      getError: (key) => fe.get(String(key)),
      clearError: (key) => fe.clear(String(key)),
      extra: taskExtra,
    },
  );

  const coreRecordValues = useMemo(
    () => buildCoreFieldRecordValues(taskEngineConfig, form),
    [form],
  );
  const layoutFormData = useMemo<Record<string, unknown>>(
    () => ({ ...coreRecordValues, ...extValues }),
    [coreRecordValues, extValues],
  );
  const additionalOwnerValues = useMemo(
    () => buildAdditionalViewRecordValues(extValues, coreRecordValues),
    [extValues, coreRecordValues],
  );

  const topUpCtx = useMemo<TopUpContext>(
    () => ({
      pageTabs: [{ slug: 'data', title: t('tenant.tasks.detail.tabs.data'), sortOrder: 0 }],
      coreFieldSlugs: taskEngineConfig.coreFieldSlugs,
    }),
    [t],
  );
  const engineLayout = useMemo(
    () => topUpDocumentLayout(layoutConfig, topUpCtx),
    [layoutConfig, topUpCtx],
  );

  const fieldsQ = useSystemModuleFields(MODULE_SLUG);
  const fieldLabel = (field: string) => resolveFieldLabel(fieldsQ.data ?? [], field);
  // Nazwa pola daty numeracji typu w komunikatach błędów zapisu i zmiany typu.
  const numberingFieldLabel = useTaskNumberingFieldLabel();

  // ── Zapis ──────────────────────────────────────────────────────────────────
  function invalidateLists() {
    void queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_ROOT, 'list'] });
    void queryClient.invalidateQueries({ queryKey: ['distinct-values', MODULE_SLUG] });
  }

  // Klik decyzji w banerze procesu w edycji: najpierw ten sam zapis co „Zapisz", okno decyzji dopiero
  // po jego sukcesie. `succeeded` / `aborted` wołane z `saveMutation.onSuccess/onError`.
  const saveBeforeDecision = useSaveBeforeDecision({
    editing,
    recordId: taskId,
    save: handleSave,
  });

  // Kontrola kartoteki (procedura tenanta `control`, slot recordType='task'): ostrzeżenie → okno
  // „Zapisz mimo to", które ponawia ten sam zapis z `warningsAccepted`. Anulowane ostrzeżenie = zapis
  // się nie odbył → okno decyzji procesu się nie otwiera, a zadanie przycisku SQL kliknięte na nowym
  // zadaniu nie rusza. `mutate` w lambdzie — `saveMutation` deklarowana niżej.
  const controlWarning = useControlWarning<SaveVariables>({
    mutate: (payload) => saveMutation.mutate(payload),
    onCancel: () => {
      saveBeforeDecision.aborted();
      settlePendingCreateRun();
    },
  });

  const saveMutation = useMutation({
    mutationKey: saveBeforeDecision.saveMutationKey,
    mutationFn: async (
      variables: SaveVariables = {},
    ): Promise<{
      id: string;
      processAutoStarting: boolean;
      processWarning?: ProcessWarning;
    } | null> => {
      const body: Record<string, unknown> = isCreate
        ? buildTaskCreateBody(form)
        : buildTaskPatchBody(form, baseForm);

      // Migawka edycji pól dodatkowych tuż przed wysyłką (edycja w trakcie żądania nie zostanie zdjęta).
      snapshotEdits();
      // Zapis: pola dodatkowe jadą, gdy są niepuste; edycja: tylko gdy różnią się od wartości serwera.
      const sendExtValues = isCreate
        ? Object.keys(extValues).length > 0
        : extValuesDiffer(extValues, task?.extValues);
      if (sendExtValues) body.extValues = extValues;
      const additionalTables = flattenAdditionalDeltas();
      if (additionalTables) {
        body.additionalTables = additionalTables;
        const parentValues = await buildAdditionalParentValues(additionalOwnerValues, 'default');
        if (parentValues) body.additionalParentValues = parentValues;
      }

      if (isCreate) {
        if (variables.warningsAccepted) body.warningsAccepted = true;
        const res = await api.post<{
          task: { id: string };
          processAutoStarting?: boolean;
          processWarning?: ProcessWarning;
        }>('/tasks', body);
        return {
          id: res.data.task.id,
          processAutoStarting: res.data.processAutoStarting ?? false,
          processWarning: res.data.processWarning,
        };
      }
      // Nic nie zmieniono — bez żądania (serwer odrzuca pusty PATCH).
      if (Object.keys(body).length === 0) return null;
      if (variables.warningsAccepted) body.warningsAccepted = true;
      await api.patch(`/tasks/${taskId}`, body);
      return { id: taskId as string, processAutoStarting: false };
    },
    onSuccess: async (saved) => {
      fe.clearAll();
      invalidateLists();
      if (saved === null) {
        toast.show({ variant: 'info', title: t('tenant.tasks.detail.toast.noChanges') });
        discardEdits();
        resetAdditionalDeltas();
        resetAddRowDrafts();
        setEditing(false);
        // Brak zmian = zapis „udany" (formularz poprawny): okno decyzji z banera ma się otworzyć.
        saveBeforeDecision.succeeded();
        return;
      }
      markSaved();
      void queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      resetAdditionalDeltas();
      resetAddRowDrafts();
      if (isCreate) {
        void queryClient.invalidateQueries({ queryKey: TASK_DETAIL_KEY(saved.id) });
        toast.show({ variant: 'success', title: t('tenant.tasks.detail.toast.created') });
        // Zadanie powstało, ale obieg domyślny typu nie wystartował — ten sam pomocnik i dymek co na
        // kartach dokumentów (teksty z drzewa `tasks`: treść o zadaniu, nie o dokumencie).
        if (saved.processWarning) {
          toast.show({
            variant: 'warning',
            title: processWarningMessage(t, saved.processWarning, 'tasks'),
          });
        }
        // Przycisk SQL kliknięty na nowym zadaniu: zadanie w tle rusza TERAZ — z id nowego zadania, PRZED
        // przejściem pod jego adres. Porażka zlecenia dostaje własny komunikat; rekord już istnieje, więc
        // przejście i tak następuje.
        if (pendingCreateRunRef.current !== null) {
          await runPendingCreateRun(saved.id);
        }
        setEditing(false);
        navigate(withMenuItem(`/tasks/${saved.id}`), {
          state: {
            replaceTab: true,
            ...(saved.processAutoStarting ? { processAutoStarting: true } : {}),
          },
        });
        return;
      }
      toast.show({ variant: 'success', title: t('tenant.tasks.detail.toast.updated') });
      // Zapis mógł zmienić dane, od których zależą prawa obiegu: flagi „Wyślij do akceptacji" /
      // „Anuluj proces" odświeżamy RAZEM z rekordem (bez czekania na nie).
      void invalidateRecordInstances(queryClient, TASKS_MODULE_CODE, saved.id);
      // Awaitowane TYLKO odświeżenie samego rekordu: baner bierze z niego typ, a okno wysyłki do
      // akceptacji (otwierane przez `succeeded()` niżej) prosi o listę procesów dla BIEŻĄCEGO typu.
      await queryClient.invalidateQueries({ queryKey: TASK_DETAIL_KEY(saved.id) });
      // `setEditing(false)` i `succeeded()` razem, bez `await` między nimi (wymóg haka).
      setEditing(false);
      saveBeforeDecision.succeeded();
    },
    onError: (err, variables) => {
      // Zapis z kliknięcia decyzji w banerze (albo przycisku SQL) zakończony błędem — okno decyzji się
      // nie otworzy, zadanie przycisku nie ruszy. O `CONTROL_WARNING` rozstrzyga user w oknie ostrzeżenia:
      // potwierdzenie ponawia zapis, anulowanie woła `onCancel` (jak karta kontrahenta).
      if (extractErrorCode(err) !== 'CONTROL_WARNING') {
        saveBeforeDecision.aborted();
        settlePendingCreateRun();
      }
      // Każda odpowiedź serwera resetuje podświetlenia z poprzedniej próby.
      const issueMessages = buildFieldIssueMessages(err, t);
      fe.applyServerError(err, {
        translate: (field, messages) =>
          issueMessages[field] ?? messages[0] ?? t('common.validationIssue.generic'),
      });
      // Kontrola kartoteki (CONTROL_ERROR / CONTROL_WARNING / CONTROL_HOOK_FAILED) — komunikat albo okno
      // ostrzeżenia już pokazane.
      if (controlWarning.handleError(err, variables ?? {})) return;
      // Zapis odrzucony, bo na zadaniu trwa zadanie przycisku SQL — czytelny komunikat z opisem zadania;
      // odświeżony stan zadań pokaże pasek blokady.
      if (runs.handleLockedError(err)) return;
      const { code, field } = parseTaskApiError(err);
      // Walidacja pól i odrzucone wartości słownikowe kończą się samym komunikatem — kody poniżej
      // (data, numeracja, odwołania, usunięte, konflikt) nigdy nie współwystępują z tymi błędami.
      const message = taskSaveErrorMessage(err, t, fieldLabel, numberingFieldLabel);
      if (code === 'DATE_ORDER_INVALID' && (field === 'plannedEndAt' || field === 'actualEndAt')) {
        fe.set(field, message);
      }
      const fieldKey = code !== undefined ? FIELD_BY_ERROR_CODE[code] : undefined;
      if (fieldKey !== undefined) fe.set(fieldKey, message);
      // Puste albo nieczytelne pole daty numeracji typu — podświetlamy je (rdzenne po kluczu
      // formularza, dodatkowe po slugu; pole spoza ekranu jest tylko w komunikacie).
      if (
        (code === 'NUMBERING_BASE_FIELD_REQUIRED' || code === 'NUMBERING_BASE_FIELD_INVALID') &&
        field !== undefined
      ) {
        fe.set(numberingBaseFormKey(field), message);
      }
      // Zadanie usunięte w międzyczasie — karta pokazuje stan serwera. Konflikt współbieżny —
      // wpisane zmiany zostają (jak w innych kartotekach), odświeża się tylko baza zmian.
      // Po usunięciu karta wychodzi z edycji: pola nieaktywne, bez „Zapisz" — zgodnie z banerem.
      if (code === 'TASK_DELETED' && taskId) {
        setEditing(false);
        void refreshFromServer(taskId);
      }
      if (code === 'TASK_CONCURRENT_EDIT' && taskId) void rebaseOnServer(taskId);
      toast.show({ variant: 'error', title: message });
    },
  });

  /** Formularz, pola dodatkowe i tabele dodatkowe z AKTUALNYCH danych serwera (forma i baza zmian). */
  function restoreFromServer(current: TaskDetailDto) {
    const next = taskFormFromDetail(current);
    setForm(next);
    setBaseForm(next);
    discardEdits();
    resetAdditionalDeltas();
    resetAddRowDrafts();
  }

  /**
   * Pobiera zadanie od nowa. Nieudane pobranie (stan zapytania „błąd" albo wyjątek) kończy się
   * komunikatem i `null` — karta nie udaje, że ma aktualne dane.
   */
  async function fetchFreshTask(id: string): Promise<TaskDetailResponse | null> {
    const key = TASK_DETAIL_KEY(id);
    try {
      await queryClient.refetchQueries({ queryKey: key });
    } catch {
      toast.show({ variant: 'error', title: t('tenant.tasks.detail.toast.refreshError') });
      return null;
    }
    const fresh = queryClient.getQueryData<TaskDetailResponse>(key);
    if (queryClient.getQueryState(key)?.status === 'error' || fresh === undefined) {
      toast.show({ variant: 'error', title: t('tenant.tasks.detail.toast.refreshError') });
      return null;
    }
    return fresh;
  }

  /** Pobiera zadanie od nowa i przywraca z niego kartę (zadanie usunięte w międzyczasie). */
  async function refreshFromServer(id: string): Promise<void> {
    setIsRebasing(true);
    try {
      const fresh = await fetchFreshTask(id);
      if (fresh) restoreFromServer(fresh.task);
    } finally {
      setIsRebasing(false);
    }
  }

  /**
   * Po konflikcie współbieżnym: baza zmian z aktualnego stanu serwera, wpisane zmiany nagłówka
   * zostają (`rebaseTaskForm`); pola dodatkowe i bufor tabel dodatkowych zachowują edycje same.
   * Na czas pobrania „Zapisz" jest nieaktywny — zapis ze starą bazą zmian cofnąłby cudzą zmianę.
   */
  async function rebaseOnServer(id: string): Promise<void> {
    const previousBase = baseForm;
    setIsRebasing(true);
    try {
      const fresh = await fetchFreshTask(id);
      if (!fresh) return;
      const freshForm = taskFormFromDetail(fresh.task);
      setForm((edited) => rebaseTaskForm(edited, previousBase, freshForm));
      setBaseForm(freshForm);
    } finally {
      setIsRebasing(false);
    }
  }

  /** Zwraca, czy zapis wystartował (false = zatrzymała go walidacja w panelu albo trwa odświeżanie). */
  function handleSave(): boolean {
    if (isRebasing) return false;
    const errors: Record<string, string> = {};
    if (form.title.trim() === '') errors.title = t('tenant.tasks.detail.titleRequired');
    if (form.taskTypeId === '') errors.taskTypeId = t('tenant.tasks.detail.typeRequired');
    const formatErrors = collectEngineFieldErrors(
      taskEngineConfig,
      form,
      catalogFields,
      extValues,
      t,
    );
    // Niepoprawna data (np. rok powyżej 9999, wklejony tekst) — błąd przy polu przed wysyłką.
    for (const field of invalidTaskDateFields(form)) {
      errors[field] = t('tenant.tasks.detail.dateInvalid');
    }
    const all = { ...formatErrors, ...errors };
    if (Object.keys(all).length > 0) {
      fe.clearAll();
      for (const [key, msg] of Object.entries(all)) fe.set(key, msg);
      toast.show({ variant: 'error', title: t('common.validationIssue.formToast') });
      return false;
    }
    saveMutation.mutate({});
    return true;
  }

  /** Kończy oczekiwanie przycisku SQL klikniętego na nowym zadaniu bez uruchamiania zadania w tle. */
  function settlePendingCreateRun(): void {
    const pending = pendingCreateRunRef.current;
    pendingCreateRunRef.current = null;
    pending?.settle();
  }

  /**
   * Zleca zadanie zapamiętane przy kliknięciu na nowym zadaniu — z id świeżo utworzonego zadania. Firma
   * zlecenia nie wymaga własnego nagłówka: prawo edycji serwer liczy z firmy samego zadania.
   */
  async function runPendingCreateRun(newTaskId: string): Promise<void> {
    const pending = pendingCreateRunRef.current;
    pendingCreateRunRef.current = null;
    if (pending === null) return;
    try {
      await runs.startRun(pending.procedureId, newTaskId);
    } finally {
      pending.settle();
    }
  }

  /**
   * Start zadania przycisku SQL. W edycji i podglądzie wołany PO bramce zapisu (edycja: najpierw zwykły
   * zapis, błąd zapisu = zadanie nie startuje; podgląd: od razu). Na nowym zadaniu zapamiętuje zadanie
   * w tle i uruchamia zwykły zapis (utworzenie) — resztę robi `saveMutation.onSuccess`.
   */
  function handleSqlButtonStart(procedureId: string): Promise<unknown> {
    if (!isCreate) return runs.startRun(procedureId);
    // Utworzenie już trwa (kliknięte „Zapisz" albo wcześniejszy przycisk) — drugi POST utworzyłby zadanie
    // drugi raz. Stan mutacji czytany na żywo (kliknięcie może wyprzedzić render).
    if (
      queryClient.isMutating({ mutationKey: saveBeforeDecision.saveMutationKey, exact: true }) > 0
    ) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      pendingCreateRunRef.current = { procedureId, settle: resolve };
      try {
        if (!handleSave()) settlePendingCreateRun();
      } catch (err) {
        // Wyjątek zanim zapis ruszył: zapamiętane zadanie nie może zostać w refie (wystartowałoby po
        // następnym, zwykłym zapisie) — czyścimy je i odrzucamy obietnicę (przycisk wraca do gotowości).
        pendingCreateRunRef.current = null;
        reject(err);
      }
    });
  }

  function backToList() {
    navigate(withMenuItem('/tasks'));
  }

  function handleCancel() {
    fe.clearAll();
    if (isCreate) {
      backToList();
      return;
    }
    setEditing(false);
    if (task !== undefined) restoreFromServer(task);
  }

  // Kontrola kartoteki przy „Zmień typ" (ten sam PATCH co zapis): ostrzeżenie → „Zapisz mimo to"
  // ponawia zmianę typu z `warningsAccepted`; anulowanie wraca do układu typu zapisanego zadania.
  const changeTypeControlWarning = useControlWarning<ChangeTypeVariables>({
    mutate: (payload) => changeTypeMutation.mutate(payload),
    onCancel: () => setPendingTypeId(null),
  });

  const changeTypeMutation = useMutation({
    mutationFn: async (variables: ChangeTypeVariables): Promise<TaskDetailDto> => {
      const res = await api.patch<{ task: TaskDetailDto }>(`/tasks/${taskId}`, {
        taskTypeId: variables.taskTypeId,
        ...(variables.warningsAccepted ? { warningsAccepted: true } : {}),
      });
      return res.data.task;
    },
    onSuccess: (updated) => {
      setChangeTypeOpen(false);
      setPendingTypeId(null);
      toast.show({ variant: 'success', title: t('tenant.tasks.detail.toast.typeChanged') });
      // Odpowiedź niesie pełny rekord z nowym typem i numerem — wstawiamy ją do pamięci podręcznej
      // (prawa akcji bez zmian) i odświeżamy listy.
      const key = TASK_DETAIL_KEY(updated.id);
      const previous = queryClient.getQueryData<TaskDetailResponse>(key);
      if (previous)
        queryClient.setQueryData<TaskDetailResponse>(key, { ...previous, task: updated });
      setForm((prev) => ({
        ...prev,
        taskTypeId: updated.taskTypeId,
        systemNumber: updated.systemNumber,
      }));
      setBaseForm((prev) => ({
        ...prev,
        taskTypeId: updated.taskTypeId,
        systemNumber: updated.systemNumber,
      }));
      invalidateLists();
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err, variables) => {
      setChangeTypeOpen(false);
      // Przy ostrzeżeniu kontroli karta zostaje na układzie typu docelowego do decyzji w oknie.
      if (extractErrorCode(err) !== 'CONTROL_WARNING') setPendingTypeId(null);
      if (changeTypeControlWarning.handleError(err, variables)) return;
      if (runs.handleLockedError(err)) return;
      toast.show({
        variant: 'error',
        title: taskErrorMessage(
          err,
          t,
          'tenant.tasks.detail.toast.changeTypeError',
          numberingFieldLabel,
        ),
      });
    },
  });

  // Kontrola przed usunięciem (procedura tenanta `delete`): ostrzeżenie → „Usuń mimo to" ponawia
  // usunięcie z `?warningsAccepted=true` (jak karta kontrahenta).
  const deleteControlWarning = useControlWarning<SaveVariables>({
    mutate: (payload) => deleteMutation.mutate(payload),
    titleKey: 'tenant.documents.controlWarning.deleteTitle',
    confirmLabelKey: 'tenant.documents.controlWarning.deleteConfirmLabel',
  });

  const deleteMutation = useMutation({
    mutationFn: async (variables: SaveVariables = {}): Promise<void> => {
      const query = variables.warningsAccepted ? '?warningsAccepted=true' : '';
      await api.delete(`/tasks/${taskId}${query}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.tasks.detail.toast.deleted') });
      invalidateLists();
      backToList();
    },
    onError: (err, variables) => {
      setConfirmDelete(false);
      if (deleteControlWarning.handleError(err, variables ?? {})) return;
      // Usuwanie odrzucone na czas zadania przycisku SQL — jak przy zapisie (opis zadania + pasek blokady).
      if (runs.handleLockedError(err)) return;
      toast.show({
        variant: 'error',
        title: taskErrorMessage(err, t, 'tenant.tasks.detail.toast.deleteError'),
      });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      await api.post(`/tasks/${taskId}/restore`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.tasks.detail.toast.restored') });
      invalidateLists();
    },
    onError: (err) => {
      // Przywrócenie odrzucone na czas zadania przycisku SQL — opis zadania + pasek blokady.
      if (runs.handleLockedError(err)) return;
      // Kody, których wspólne teksty zadań (pisane pod zapis) nie pasują do przywracania: typ
      // usunięty lub wyłączony (najpierw przywróć / włącz typ), numer zajęty przez inne zadanie.
      const restoreKey = RESTORE_ERROR_KEYS[parseTaskApiError(err).code ?? ''];
      const message = restoreKey
        ? t(restoreKey)
        : taskErrorMessage(err, t, 'tenant.tasks.detail.toast.restoreError');
      toast.show({ variant: 'error', title: message });
    },
    // Karta odświeża się także po błędzie — zadanie mogło wrócić albo zmienić typ w międzyczasie.
    onSettled: () => {
      if (taskId !== null) {
        void queryClient.invalidateQueries({ queryKey: TASK_DETAIL_KEY(taskId) });
      }
    },
  });

  // ── Stany: ładowanie, błąd, wybór typu ─────────────────────────────────────
  if (!isCreate && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="task-detail-loading">
        <Spinner />
      </div>
    );
  }

  // Ekran błędu tylko gdy nie ma żadnych danych — nieudane odświeżenie przy już wczytanym zadaniu
  // nie może zastąpić karty z wpisanymi zmianami (komunikat pokazuje toast).
  if (!isCreate && detailQ.isError && detailQ.data === undefined) {
    const { status, code } = parseTaskApiError(detailQ.error);
    const messageKey =
      status === 404 || code === 'TASK_NOT_FOUND'
        ? 'tenant.tasks.detail.notFound'
        : status === 403
          ? 'tenant.tasks.detail.noAccess'
          : 'tenant.tasks.detail.loadError';
    return (
      <div className="m-6 space-y-4" data-testid="task-detail-error">
        <Alert tone="error">{t(messageKey)}</Alert>
        <Button
          variant="secondary"
          leftIcon={<ArrowLeft size={14} aria-hidden="true" />}
          onClick={backToList}
          data-testid="task-detail-error-back"
        >
          {t('common.back')}
        </Button>
      </div>
    );
  }

  if (isCreate && createType === null) {
    // Brak (poprawnego) typu w adresie: najpierw wybór typu — to samo okno co po „Dodaj".
    return (
      <div className="p-6" data-testid="task-detail-pick-type">
        {typesQ.isLoading ? (
          <div className="flex justify-center p-12">
            <Spinner />
          </div>
        ) : typesQ.isError ? (
          <Alert tone="error">
            {taskErrorMessage(typesQ.error, t, 'tenant.tasks.typePicker.loadError')}
          </Alert>
        ) : (
          <TaskTypePickerModal
            open
            preferredCompanyId={null}
            onClose={backToList}
            onSelect={(typeId) => {
              const next = new URLSearchParams(searchParams);
              next.set('taskTypeId', typeId);
              setSearchParams(next, { replace: true });
            }}
          />
        )}
      </div>
    );
  }

  // Typ wybrany: pola dopiero po wczytaniu wartości domyślnych i zainicjowaniu formularza nimi.
  if (
    isCreate &&
    createType !== null &&
    (recordDefaults.status !== 'ready' || initializedTypeId !== createType.id)
  ) {
    return <NewRecordDefaultsStatus defaults={recordDefaults} testIdPrefix="task-detail" />;
  }

  const processRenderers = buildProcessLayoutRenderers({
    moduleCode: TASKS_MODULE_CODE,
    recordId: task?.id ?? null,
    processesActive,
    typeId: task?.taskTypeId ?? null,
    beforeDecision: saveBeforeDecision.beforeDecision,
  });

  // Tabele dodatkowe (blok `additional_view` i starszy `satellite_view`) — jedno wyrenderowanie.
  const renderAdditionalTable: ContextRenderers[string] = ({ element }) => {
    const tableSlug =
      typeof element?.settings?.tableSlug === 'string' ? element.settings.tableSlug : '';
    return tableSlug ? (
      <div className="col-span-full">
        <AdditionalTableTab
          tableSlug={tableSlug}
          parentId={taskId}
          editable={editing || isCreate}
          buffered
          onDeltasChange={handleAdditionalDeltasChange}
          getBufferedValue={getBufferedValue}
          getAddRowDraft={getAddRowDraft}
          onAddRowDraftChange={handleAddRowDraftChange}
          parentValues={additionalOwnerValues}
          parentKey="default"
          columnSettings={element.settings}
        />
      </div>
    ) : null;
  };

  // Historia zmian (blok `task_audit_view` i ogólny `audit_log_view`) — jedno wyrenderowanie.
  const renderAudit: ContextRenderers[string] = () =>
    taskId ? (
      <SystemModuleAuditTab moduleSlug={MODULE_SLUG} recordId={taskId} testIdPrefix="task-audit" />
    ) : (
      <SaveFirstNotice message={t('tenant.tasks.detail.tabDisabled.save')} />
    );

  const headerTitle = isCreate
    ? t('tenant.tasks.detail.createTitle')
    : `${task?.systemNumber ?? ''} — ${task?.title ?? ''}`;

  return (
    <div className="space-y-4 p-6" data-testid="task-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={t('common.back')}
            onClick={backToList}
            data-testid="task-detail-back"
          >
            <ArrowLeft />
          </IconButton>
          <div>
            <h1 className="text-2xl font-semibold text-text-primary">{headerTitle}</h1>
            {task && (
              <div className="mt-1 flex items-center gap-2">
                <Badge tone={STATUS_TONE[task.status]}>{statusLabel(t, task.status)}</Badge>
                <Badge tone={PRIORITY_TONE[task.priority]}>{priorityLabel(t, task.priority)}</Badge>
                {isDeleted && <Badge tone="danger">{t('tenant.tasks.detail.deletedBadge')}</Badge>}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!editing && !isCreate && !isDeleted && (
            <>
              <PermissionButton
                allowed={canEditRecord}
                reason={t('tenant.permissions.denied.tooltip')}
                variant="secondary"
                leftIcon={<Pencil size={14} aria-hidden="true" />}
                onClick={() => setEditing(true)}
                disabled={recordLocked}
                data-testid="task-detail-edit-btn"
              >
                {t('common.edit')}
              </PermissionButton>
              <PermissionButton
                allowed={canDeleteRecord}
                reason={t('tenant.permissions.denied.tooltip')}
                variant="danger"
                leftIcon={<Trash2 size={14} aria-hidden="true" />}
                onClick={() => setConfirmDelete(true)}
                disabled={recordLocked}
                data-testid="task-detail-delete-btn"
              >
                {t('common.delete')}
              </PermissionButton>
            </>
          )}
          {isDeleted && canDeleteRecord && (
            <Button
              variant="primary"
              leftIcon={<RotateCcw size={14} aria-hidden="true" />}
              loading={restoreMutation.isPending}
              disabled={restoreMutation.isPending || recordLocked}
              onClick={() => setConfirmRestore(true)}
              data-testid="task-detail-restore-btn"
            >
              {t('tenant.tasks.detail.restoreAction')}
            </Button>
          )}
          {(editing || isCreate) && (
            <>
              <Button
                variant="secondary"
                leftIcon={<XIcon size={14} aria-hidden="true" />}
                onClick={handleCancel}
                disabled={saveMutation.isPending}
                data-testid="task-detail-cancel-btn"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                leftIcon={<Save size={14} aria-hidden="true" />}
                onClick={handleSave}
                disabled={saveMutation.isPending || isRebasing}
                data-testid="task-detail-save-btn"
              >
                {t('common.save')}
              </Button>
            </>
          )}
        </div>
      </div>

      {isDeleted && <Alert tone="warning">{t('tenant.tasks.detail.deletedNotice')}</Alert>}

      {/* Zadanie przycisku SQL na zadaniu (w kolejce lub w toku): zadania nie da się zapisać ani usunąć,
          „Edytuj” i „Usuń” są nieaktywne; pasek znika po zakończeniu zadania w tle. */}
      {runs.active ? (
        <RecordRunLockBanner
          run={runs.active}
          label={runs.labelFor(runs.active)}
          refreshFailed={runs.isError}
        />
      ) : null}

      {/* Akceptacja rekordu (start / przebieg / decyzja / anulowanie) — baner nad formularzem.
          Tylko dla zapisanego zadania i gdy moduł Procesy jest aktywny. */}
      {!isCreate && task && processesActive ? (
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <ProcessApprovalBanner
              moduleCode={TASKS_MODULE_CODE}
              recordId={task.id}
              typeId={task.taskTypeId}
              beforeDecision={saveBeforeDecision.beforeDecision}
            />
          </div>
          <ProcessAutostartIndicator starting={autostart.starting} />
        </div>
      ) : null}

      <RecursiveFormRenderer
        layoutConfig={engineLayout}
        activeTabId={tab}
        onTabChange={setTab}
        myRoleId={myRoleId}
        isSuperUser={isSuperUser}
        renderField={renderField}
        formData={layoutFormData}
        contextRenderers={{
          // Tabele dodatkowe: buforowane (dostępne już przy tworzeniu), delty jadą w POST / PATCH.
          additional_view: renderAdditionalTable,
          satellite_view: renderAdditionalTable,
          additional_query_view: ({ element }) => {
            const viewSlug =
              typeof element?.settings?.viewSlug === 'string' ? element.settings.viewSlug : '';
            return viewSlug ? (
              <div className="col-span-full">
                <AdditionalViewTab
                  viewSlug={viewSlug}
                  recordId={taskId}
                  recordValues={additionalOwnerValues}
                  columnSettings={element.settings}
                />
              </div>
            ) : null;
          },
          // „Historia zmian" jako przenośny blok kartoteki; ogólny `audit_log_view` — zapas dla układów
          // sprzed bloków.
          task_audit_view: renderAudit,
          audit_log_view: renderAudit,
          // Załączniki, podgląd pliku i powiązania rekordów — bloki self-persisting (własne zapisy,
          // niezależne od zapisu formularza), dopiero dla zapisanego zadania. Firma z zadania
          // (zadanie nie zmienia firmy), także w trybie „Wszystkie firmy”.
          attachments_view: () =>
            task ? (
              <AttachmentsSection ownerType="task" ownerId={task.id} companyId={task.companyId} />
            ) : null,
          ...buildDocumentPreviewContextRenderer({
            ownerType: 'task',
            ownerId: task?.id,
            companyId: task?.companyId,
            extValues,
          }),
          record_links_view: ({ element }) =>
            task ? (
              <RecordLinksSection
                ownerType="task"
                ownerId={task.id}
                companyId={task.companyId}
                config={parseRecordLinksElementConfig(element.settings)}
              />
            ) : null,
          // Klocki procesowe („Proces akceptacji", „Przycisk akceptacji") — wspólny kształt z
          // dokumentami; treść dopiero po wczytaniu zadania (typ znany) i przy aktywnym module Procesy.
          ...processRenderers,
          // Klocek „Przycisk SQL”: zadanie w tle na tym zadaniu. W edycji klik najpierw zapisuje rekord
          // (ta sama bramka co przycisk akceptacji; błąd zapisu = zadanie nie rusza), w podglądzie rusza od
          // razu, na nowym zadaniu — utworzenie, zadanie, przejście. Prawo: ten sam sygnał co „Edytuj”
          // (serwer i tak sprawdza); na nowym zadaniu aktywny — prawo do typu rozstrzyga serwer przy
          // utworzeniu, jak u kontrahenta i pracownika. Usunięte zadanie: bez prawa.
          // Klocek bez procedury / napisu: nic. „Zapisz" w toku też blokuje przycisk.
          sql_button: ({ element }) => {
            const cfg = parseSqlButtonSettings(element.settings);
            if (cfg === null) return null;
            return (
              <SqlButton
                label={cfg.label}
                procedureId={cfg.procedureId}
                active={runs.active}
                lastRun={runs.lastByProcedure.find((run) => run.procedureId === cfg.procedureId)}
                canEdit={isCreate || (canEditRecord && !isDeleted)}
                starting={runs.starting || saveMutation.isPending}
                beforeStart={isCreate ? undefined : saveBeforeDecision.beforeDecision}
                onStart={(procedureId) => handleSqlButtonStart(procedureId)}
                testIdPrefix={`sql-button-${element.id}`}
              />
            );
          },
        }}
      />

      {changeTypeOpen && task && (
        <TaskTypePickerModal
          open
          companyId={task.companyId}
          excludeTypeId={task.taskTypeId}
          title={t('tenant.tasks.detail.changeTypeTitle')}
          confirmLabel={t('tenant.tasks.detail.changeTypeConfirm')}
          onClose={() => setChangeTypeOpen(false)}
          onSelect={(typeId) => {
            setPendingTypeId(typeId);
            changeTypeMutation.mutate({ taskTypeId: typeId });
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('tenant.tasks.detail.delete.title')}
        message={t('tenant.tasks.detail.delete.message', { name: task?.title ?? '' })}
        confirmLabel={t('common.delete')}
        variant="danger"
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate({});
        }}
        onClose={() => setConfirmDelete(false)}
      />

      <ConfirmDialog
        open={confirmRestore}
        title={t('tenant.tasks.detail.restore.title')}
        message={t('tenant.tasks.detail.restore.message', { name: task?.title ?? '' })}
        confirmLabel={t('tenant.tasks.detail.restoreAction')}
        variant="primary"
        onConfirm={() => {
          setConfirmRestore(false);
          restoreMutation.mutate();
        }}
        onClose={() => setConfirmRestore(false)}
      />

      {controlWarning.dialog}
      {changeTypeControlWarning.dialog}
      {deleteControlWarning.dialog}
    </div>
  );
}
